import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  FormModalConfirmation,
  FormModalContainer,
  useCloseConfirm,
} from '../../../Form'
import { FormikForm } from '../../../Formik'
import { MODAL_HEADER_VARIANT } from '../../../Modal'

import { QA_NAME_DOC_FLOW_MAPPING_RECIPIENT_MODAL } from './DocFlowMappingRecipientModal.constants'
import {
  useDocFlowMappingRecipientModalPreview,
  useDocFlowMappingRecipientModalQueue,
} from './DocFlowMappingRecipientModal.hooks'
import {
  getDocFlowMappingRecipientModalCounterKey,
  getDocFlowMappingRecipientModalInitialValues,
} from './DocFlowMappingRecipientModal.utils'
import { DocFlowMappingRecipientModalNavigation } from './DocFlowMappingRecipientModalNavigation'
import { DocFlowMappingRecipientModalPanel } from './DocFlowMappingRecipientModalPanel'
import { DocFlowMappingRecipientModalPreview } from './DocFlowMappingRecipientModalPreview'

import * as styles from './DocFlowMappingRecipientModal.module.css'

function DocFlowMappingRecipientModal({
  activeFile,
  activeFileBytes,
  filterValues,
  initialItem,
  initialPosition,
  initialTotal,
  ordering,
  onClose,
}) {
  const { t } = useTranslation()

  const [isDirty, setDirty] = useState(false)
  const [pendingStep, setPendingStep] = useState(null)

  const {
    handleClose,
    handleNext,
    handlePrevious,
    handleSubmit,
    hasNext,
    hasPrevious,
    isLoading,
    item,
    position,
    total,
  } = useDocFlowMappingRecipientModalQueue({
    filterValues,
    initialItem,
    initialPosition,
    initialTotal,
    ordering,
    uploadingId: activeFile.itemId,
    onClose,
  })

  const { hasPreviewError, previewFile } =
    useDocFlowMappingRecipientModalPreview({
      activeFileBytes,
      pagesRange: item.pages_range,
    })

  const { isCloseConfirm, setCloseConfirm } = useCloseConfirm({
    onClose: handleClose,
  })

  function getStepHandler(handleStep) {
    return () => {
      if (isDirty) {
        setPendingStep(() => handleStep)
      } else {
        handleStep()
      }
    }
  }

  function handleCancelStep() {
    setPendingStep(null)
  }

  function handleConfirmStep() {
    setPendingStep(null)
    pendingStep()
  }

  return (
    <FormikForm
      enableReinitialize
      Container={FormModalContainer}
      containerProps={{
        boxProps: { className: styles.box },
        boxSize: null,
        headerProps: {
          actions: (
            <DocFlowMappingRecipientModalNavigation
              counterText={t(
                getDocFlowMappingRecipientModalCounterKey(filterValues),
                { current: position, total }
              )}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              isLoading={isLoading}
              onNext={getStepHandler(handleNext)}
              onPrevious={getStepHandler(handlePrevious)}
            />
          ),
        },
        headerVariant: MODAL_HEADER_VARIANT.EDIT,
        isCloseConfirm,
        modal: pendingStep ? (
          <FormModalConfirmation
            onCancel={handleCancelStep}
            onSubmit={handleConfirmStep}
          />
        ) : null,
        qaName: QA_NAME_DOC_FLOW_MAPPING_RECIPIENT_MODAL,
        setCloseConfirm,
        title: t('DocFlow.MappingRecipientModalTitleKey'),
        onClose: handleClose,
        onIsDirtyChange: setDirty,
      }}
      formProps={{ className: styles.form }}
      initialValues={getDocFlowMappingRecipientModalInitialValues(item)}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <DocFlowMappingRecipientModalPanel
        activeFile={activeFile}
        hasNext={hasNext}
        isLoading={isLoading}
        item={item}
        onSkip={getStepHandler(handleNext)}
      />
      <DocFlowMappingRecipientModalPreview
        hasPreviewError={hasPreviewError}
        previewFile={previewFile}
      />
    </FormikForm>
  )
}

export default DocFlowMappingRecipientModal
